import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { PromptPowerUp } from '../../components/PromptPowerUp';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Film, User, Mic, Download, Upload, Trash2, RefreshCw, Sparkles, Clock, FolderOpen, Radio, Headphones, Play } from 'lucide-react';
import { ProcessingAnimation } from '../../components/ProcessingAnimation';
import { API, LANGUAGES, downloadFile } from './shared';
import { useIdempotencyKey } from '../../lib/useIdempotencyKey';
import { useStudioErrorHandler } from '../../components/SubmissionUncertainModal';
import { useHubPricing, formatCreditRange } from '../../lib/useHubPricing';
import { useStudioHealth, StudioHealthBanner } from '../../lib/useStudioHealth';

export function AvatarStudio() {
  const [tab, setTab] = useState('avatars');
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [videos, setVideos] = useState([]);

  const fetchAvatars = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/creative-studio/avatars`); setAvatars(res.data.avatars || []); } catch { /* */ }
  }, []);
  const fetchVoices = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/creative-studio/voices`); setVoices(res.data.voices || []); } catch { /* */ }
  }, []);
  const fetchVideos = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/creative-studio/avatar-videos`); setVideos(res.data.videos || []); } catch { /* */ }
  }, []);

  useEffect(() => { fetchAvatars(); fetchVoices(); fetchVideos(); }, [fetchAvatars, fetchVoices, fetchVideos]);

  // v2.7.35 — Empty-state CTA in Create sub-tab fires this event so we can
  // route the user back to the Avatars sub-tab to upload their first avatar.
  useEffect(() => {
    const handler = (e) => { if (e?.detail) setTab(String(e.detail)); };
    window.addEventListener('avatar-studio:switch-tab', handler);
    return () => window.removeEventListener('avatar-studio:switch-tab', handler);
  }, []);

  const SUB_TABS = [
    { key: 'avatars', label: 'Avatars', icon: User },
    { key: 'voices', label: 'Voices', icon: Mic },
    { key: 'create', label: 'Create', icon: Film },
    { key: 'podcast', label: 'Podcast', icon: Radio },
    { key: 'avlibrary', label: 'Library', icon: FolderOpen },
  ];

  return (
    <div data-testid="avatar-studio">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-text-primary mb-1">Avatar Studio</h2>
        <p className="text-xs text-gray-medium mb-3">Create professional videos with custom avatars and voices</p>
        <div className="flex gap-1.5 border-b border-gray-border pb-0">
          {SUB_TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn("flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px",
                tab === t.key ? "border-text-primary text-text-primary" : "border-transparent text-gray-medium hover:text-text-primary")}
              data-testid={`avatar-tab-${t.key}`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === 'avatars' && <AvatarsTab avatars={avatars} onRefresh={fetchAvatars} />}
      {tab === 'voices' && <VoicesTab voices={voices} onRefresh={fetchVoices} />}
      {tab === 'create' && <CreateVideoTab avatars={avatars} voices={voices} onCreated={fetchVideos} />}
      {tab === 'podcast' && <PodcastTab avatars={avatars} voices={voices} onCreated={fetchVideos} />}
      {tab === 'avlibrary' && <AvatarLibraryTab videos={videos} onRefresh={fetchVideos} />}
    </div>
  );
}

function AvatarsTab({ avatars, onRefresh }) {
  const [name, setName] = useState('');
  const [avatarType, setAvatarType] = useState('uploaded');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!name.trim() || !file) { toast.error('Name and video file required'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('name', name); fd.append('avatar_type', avatarType); fd.append('file', file);
    try {
      await axios.post(`${API}/api/creative-studio/avatar/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Avatar uploaded!'); setName(''); setFile(null); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
  };

  const deleteAvatar = async (id) => {
    if (!window.confirm('Delete avatar?')) return;
    try { await axios.delete(`${API}/api/creative-studio/avatar/${id}`); toast.success('Deleted'); onRefresh(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="avatars-tab">
      <Card className="bg-white border-gray-border">
        <CardContent className="pt-4 space-y-4">
          <div><h3 className="text-sm font-bold text-text-primary">Avatar Management</h3><p className="text-xs text-gray-medium">Upload and manage your AI avatars</p></div>
          <div><Label className="text-xs">Avatar Name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Professional Avatar" className="mt-1 text-sm" data-testid="avatar-name" /></div>
          <div><Label className="text-xs">Avatar Type</Label>
            <Select value={avatarType} onValueChange={setAvatarType}><SelectTrigger className="mt-1" data-testid="avatar-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="uploaded">Uploaded</SelectItem><SelectItem value="ai_generated">AI Generated</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs">Avatar Video</Label>
            <Input type="file" accept="video/mp4,video/mov,video/webm" onChange={e => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="avatar-file" />
            <p className="text-[10px] text-gray-400 mt-1">Upload a short video clip (5-15 seconds recommended)</p></div>
          <Button className="w-full" onClick={upload} disabled={uploading || !name.trim() || !file} data-testid="upload-avatar-btn">
            {uploading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Upload Avatar
          </Button>
        </CardContent>
      </Card>
      <Card className="bg-white border-gray-border">
        <CardContent className="pt-4">
          <h3 className="text-sm font-bold text-text-primary mb-3">Your Avatars ({avatars.length})</h3>
          {avatars.length === 0 ? (
            <div className="text-center py-8"><User className="h-10 w-10 mx-auto mb-2 text-gray-200" /><p className="text-xs text-gray-medium">No avatars yet</p></div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {avatars.map(a => (
                <div key={a.avatar_id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border" data-testid={`avatar-${a.avatar_id}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center flex-shrink-0"><User className="h-5 w-5 text-blue-600" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{a.name}</p><p className="text-[10px] text-gray-medium capitalize">{a.avatar_type} · {a.status}</p></div>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteAvatar(a.avatar_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function VoicesTab({ voices, onRefresh }) {
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en');
  const [gender, setGender] = useState('neutral');
  const [file, setFile] = useState(null);
  const [cloning, setCloning] = useState(false);

  const cloneVoice = async () => {
    if (!name.trim() || !file) { toast.error('Name and voice sample required'); return; }
    setCloning(true);
    const fd = new FormData();
    fd.append('name', name); fd.append('language', language); fd.append('gender', gender); fd.append('file', file);
    try {
      await axios.post(`${API}/api/creative-studio/voice/clone`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Voice cloning started!'); setName(''); setFile(null); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Clone failed'); } finally { setCloning(false); }
  };

  const deleteVoice = async (id) => {
    if (!window.confirm('Delete voice?')) return;
    try { await axios.delete(`${API}/api/creative-studio/voice/${id}`); toast.success('Deleted'); onRefresh(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="voices-tab">
      <Card className="bg-white border-gray-border">
        <CardContent className="pt-4 space-y-4">
          <div><h3 className="text-sm font-bold text-text-primary">Voice Management</h3><p className="text-xs text-gray-medium">Clone voices and manage your voice library</p></div>
          <div><Label className="text-xs">Voice Name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Professional Narrator" className="mt-1 text-sm" data-testid="voice-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Language</Label>
              <Select value={language} onValueChange={setLanguage}><SelectTrigger className="mt-1" data-testid="voice-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Gender</Label>
              <Select value={gender} onValueChange={setGender}><SelectTrigger className="mt-1" data-testid="voice-gender"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="neutral">Neutral</SelectItem></SelectContent></Select></div>
          </div>
          <div><Label className="text-xs">Voice Sample</Label>
            <Input type="file" accept="audio/wav,audio/mp3,audio/m4a,audio/ogg,video/mp4,video/mov" onChange={e => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="voice-file" />
            <p className="text-[10px] text-gray-400 mt-1">Upload a clear audio sample (30-60 seconds recommended)</p></div>
          <Button className="w-full" onClick={cloneVoice} disabled={cloning || !name.trim() || !file} data-testid="clone-voice-btn">
            {cloning ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mic className="h-4 w-4 mr-2" />}Clone Voice
          </Button>
        </CardContent>
      </Card>
      <Card className="bg-white border-gray-border">
        <CardContent className="pt-4">
          <h3 className="text-sm font-bold text-text-primary mb-3">Your Voices ({voices.length})</h3>
          {voices.length === 0 ? (
            <div className="text-center py-8"><Mic className="h-10 w-10 mx-auto mb-2 text-gray-200" /><p className="text-xs text-gray-medium">No cloned voices yet</p></div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {voices.map(v => (
                <div key={v.voice_id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border" data-testid={`voice-${v.voice_id}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center flex-shrink-0"><Headphones className="h-5 w-5 text-pink-600" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{v.name}</p>
                    <p className="text-[10px] text-gray-medium capitalize">{LANGUAGES.find(l => l.code === v.language)?.label || v.language} · {v.gender} · {v.status}</p></div>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteVoice(v.voice_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CreateVideoTab({ avatars, voices, onCreated }) {
  const [avatarId, setAvatarId] = useState('');
  const [voiceId, setVoiceId] = useState('default');
  const [quality, setQuality] = useState('480p');
  const [duration, setDuration] = useState('30');
  const [script, setScript] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const pollRef = useRef(null);

  // v2.7.35 — Pre-select the first available avatar so the panel is
  // submittable with only a script typed. The audit flagged that an
  // empty avatarId gate made TALKING_VIDEO unreachable from the UI even
  // though upstream health was 200.
  useEffect(() => {
    if (!avatarId && Array.isArray(avatars) && avatars.length > 0) {
      const first = avatars[0];
      if (first?.avatar_id) setAvatarId(first.avatar_id);
    }
  }, [avatars, avatarId]);

  // v3.3 (2026-07-01): auto-populate script + duration when the user arrives here
  // via the "Turn into Video" button on the Influencer Studio.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('konnect.pending_avatar_script');
      if (raw) {
        const payload = JSON.parse(raw);
        if (payload?.script) setScript(String(payload.script));
        if (payload?.duration_seconds) setDuration(String(payload.duration_seconds));
        sessionStorage.removeItem('konnect.pending_avatar_script');
        toast.success('Script loaded from Influencer Studio — pick an avatar and click Create Video.');
      }
    } catch { /* ignore */ }
  }, []);

  // v3.0 Kaharian: stable Idempotency-Key + uncertain-submission modal
  const idempotencyKey = useIdempotencyKey('konnect-avatar-video', [
    avatarId, voiceId, quality, duration, script,
  ]);
  const { handleStudioError, SubmissionUncertainDialog } = useStudioErrorHandler();
  const { getPrice, isLoading: pricingLoading } = useHubPricing();
  // Duration-aware pricing: Hub bills TALKING_VIDEO.short for ≤30s, .standard otherwise.
  const talkingRow = getPrice(parseInt(duration || '30', 10) <= 30 ? 'TALKING_VIDEO.short' : 'TALKING_VIDEO.standard');

  // v3.2: shared provider-health probe + auto-retry
  const { health, cooldown, onProvidersUnavailable, pendingRetryRef } = useStudioHealth(
    'TALKING_VIDEO',
    { onRecover: () => create() }
  );

  const create = async () => {
    if (!avatarId || !script.trim()) { toast.error('Select avatar and enter script'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/creative-studio/avatar/create-video`, {
        avatar_id: avatarId, voice_id: voiceId, script, quality, duration_seconds: parseInt(duration),
        idempotency_key: idempotencyKey,
      });
      setResult(res.data);
      // v3.3 (2026-07-01): the endpoint returns task_id + status='processing' for async
      // generations. Previously the UI just displayed "Processing..." forever with no
      // polling. Now we poll /task-status every 5s until completed/failed or 8min timeout.
      if (res.data?.task_id && !res.data.url) {
        toast.success('Video generation started — this typically takes 1-3 minutes.');
        const deadline = Date.now() + 8 * 60 * 1000; // 8 min max
        pollRef.current = setInterval(async () => {
          if (Date.now() > deadline) {
            clearInterval(pollRef.current);
            toast.error('Video is taking longer than expected. Check the Library in a few minutes — no credits charged until ready.');
            setCreating(false);
            setResult(null);
            return;
          }
          try {
            const poll = await axios.get(`${API}/api/creative-studio/task-status/${res.data.task_id}`);
            const pd = poll.data;
            if ((pd.status === 'completed' || pd.status === 'ready') && pd.url) {
              clearInterval(pollRef.current);
              setResult({ ...res.data, url: pd.url, status: 'completed' });
              setCreating(false);
              toast.success('Avatar video ready!');
              onCreated();
            } else if (pd.status === 'failed' || pd.status === 'error') {
              clearInterval(pollRef.current);
              setResult(null);
              setCreating(false);
              toast.error('Avatar video generation failed. Credits will be refunded.');
            }
          } catch { /* keep polling through transient errors */ }
        }, 5000);
      } else {
        toast.success(res.data.url ? 'Video created!' : 'Processing started');
        setCreating(false);
        onCreated();
      }
    } catch (err) {
      setCreating(false);
      const detail = err?.response?.data?.detail;
      const isProvidersDown = err?.response?.status === 503 && typeof detail?.code === 'string' && detail.code.endsWith('_PROVIDERS_UNAVAILABLE');
      if (isProvidersDown) {
        onProvidersUnavailable(detail, { armRetry: true });
        toast.error(`${detail.message || 'Temporarily unavailable'} · Auto-retrying in 30s…`, { duration: 5000, id: 'avatar-video-providers-down' });
      } else {
        handleStudioError(err, { category: 'AVATAR', idempotencyKey, fallbackMessage: 'Avatar video creation failed' });
      }
    }
  };

  // Cleanup polling on unmount.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const QUALITIES = [
    { value: '480p', label: '480p — Standard' },
    { value: '720p', label: '720p — HD' },
    { value: '1080p', label: '1080p — Full HD' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="create-video-tab">
      <SubmissionUncertainDialog />
      <div className="lg:col-span-2">
        <StudioHealthBanner category="TALKING_VIDEO" health={health} cooldown={cooldown} pendingRetry={pendingRetryRef.current} />
      </div>
      {avatars.length === 0 && (
        <div className="lg:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="avatar-empty-state">
          <User className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">Upload an avatar first</p>
            <p className="text-xs text-amber-800 mt-0.5">To generate talking-head videos, add at least one avatar. Head over to the <button type="button" className="underline font-medium hover:text-amber-950" onClick={() => window.dispatchEvent(new CustomEvent('avatar-studio:switch-tab', { detail: 'avatars' }))} data-testid="avatar-empty-state-cta">Avatars</button> tab to upload one (video or photo) and it will appear here automatically.</p>
          </div>
        </div>
      )}
      <Card className="bg-white border-gray-border">
        <CardContent className="pt-4 space-y-4">
          <div><h3 className="text-sm font-bold text-text-primary">Create Avatar Video</h3><p className="text-xs text-gray-medium">Generate a talking head video with your avatar</p></div>
          <div><Label className="text-xs">Select Avatar *</Label>
            <Select value={avatarId} onValueChange={setAvatarId}><SelectTrigger className="mt-1" data-testid="select-avatar"><SelectValue placeholder="Choose an avatar" /></SelectTrigger>
              <SelectContent>{avatars.map(a => <SelectItem key={a.avatar_id} value={a.avatar_id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Voice Profile (Optional)</Label>
            <Select value={voiceId} onValueChange={setVoiceId}><SelectTrigger className="mt-1" data-testid="select-voice"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="default">Use default voice</SelectItem>{voices.map(v => <SelectItem key={v.voice_id} value={v.voice_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Quality</Label>
              <Select value={quality} onValueChange={setQuality}><SelectTrigger className="mt-1 text-xs" data-testid="select-quality"><SelectValue /></SelectTrigger>
                <SelectContent>{QUALITIES.map(q => <SelectItem key={q.value} value={q.value} className="text-xs">{q.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Estimated Duration (seconds)</Label>
              <Input type="number" value={duration} onChange={e => setDuration(e.target.value)} min="5" max="300" className="mt-1 text-sm" data-testid="video-duration" /></div>
          </div>
          <div><Label className="text-xs">Script (Text-to-Speech)</Label>
            <PromptPowerUp contextType="general">
            <Textarea value={script} onChange={e => setScript(e.target.value)} placeholder="Enter what the avatar should say..."
              className="mt-1 min-h-[120px] text-sm" data-testid="avatar-script" />
            </PromptPowerUp></div>
          <Button className="w-full h-11" onClick={create} disabled={creating || !avatarId || !script.trim() || pricingLoading || !talkingRow || !health.available} data-testid="generate-avatar-btn" title={!script.trim() ? 'Enter a script to continue' : (!avatarId ? 'Waiting for avatars to load…' : (!health.available ? 'Provider temporarily unavailable' : ''))}>
            {creating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {!health.available ? `Unavailable${cooldown > 0 ? ` · ${cooldown}s` : ''}` : (!pricingLoading && !talkingRow ? 'Unavailable' : `Create Video — ${pricingLoading ? '…' : formatCreditRange(talkingRow)}`)}
          </Button>
        </CardContent>
      </Card>
      <Card className="bg-white border-gray-border">
        <CardContent className="p-6">
          {result?.url ? (
            <div><video src={result.url} controls className="w-full rounded-lg mb-3" />
              <div className="flex gap-2"><Button size="sm" variant="outline" className="flex-1" onClick={() => downloadFile(result.url, `konnect_avatar_${Date.now()}.mp4`)}><Download className="h-3.5 w-3.5 mr-1" />Download</Button></div></div>
          ) : result?.task_id ? (
            <ProcessingAnimation
              title="Creating Avatar Video"
              messages={['Preparing your avatar...', 'Generating lip sync...', 'Rendering video frames...', 'Applying voice synthesis...', 'Compositing final video...', 'This typically takes 1-3 minutes...']}
              subtitle={`Task ID: ${result.task_id.substring(0, 12)}...`}
            />
          ) : (
            <div className="text-center py-12"><Film className="h-16 w-16 mx-auto mb-4 text-gray-200" /><p className="text-sm text-gray-medium">Avatar video preview</p><p className="text-xs text-gray-400 mt-1">Select an avatar and enter a script to get started</p></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PodcastTab({ avatars, voices, onCreated }) {
  const [title, setTitle] = useState('');
  const [avatarId, setAvatarId] = useState('');
  const [voiceId, setVoiceId] = useState('default');
  const [quality, setQuality] = useState('480p');
  const [duration, setDuration] = useState('30');
  const [script, setScript] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const pollRef = useRef(null);

  // v3.0 Kaharian: stable Idempotency-Key + uncertain-submission modal
  const idempotencyKey = useIdempotencyKey('konnect-podcast', [
    title, avatarId, voiceId, quality, duration, script,
  ]);
  const { handleStudioError, SubmissionUncertainDialog } = useStudioErrorHandler();
  const { getPrice, isLoading: pricingLoading } = useHubPricing();
  const talkingRow = getPrice(parseInt(duration || '30', 10) <= 30 ? 'TALKING_VIDEO.short' : 'TALKING_VIDEO.standard');

  // v3.2: shared provider-health probe + auto-retry
  const { health, cooldown, onProvidersUnavailable, pendingRetryRef } = useStudioHealth(
    'TALKING_VIDEO',
    { onRecover: () => create() }
  );

  const create = async () => {
    if (!avatarId || !script.trim()) { toast.error('Select avatar and enter script'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/creative-studio/podcast/create`, {
        title: title || 'My Podcast Episode', avatar_id: avatarId, voice_id: voiceId,
        script, quality, duration_seconds: parseInt(duration),
        idempotency_key: idempotencyKey,
      });
      setResult(res.data);
      // v3.3 (2026-07-01): poll async task-status until completion.
      if (res.data?.task_id && !res.data.url) {
        toast.success('Podcast generation started — this typically takes 2-4 minutes.');
        const deadline = Date.now() + 10 * 60 * 1000; // 10 min max
        pollRef.current = setInterval(async () => {
          if (Date.now() > deadline) {
            clearInterval(pollRef.current);
            toast.error('Podcast is taking longer than expected. Check the Library shortly.');
            setCreating(false);
            setResult(null);
            return;
          }
          try {
            const poll = await axios.get(`${API}/api/creative-studio/task-status/${res.data.task_id}`);
            const pd = poll.data;
            if ((pd.status === 'completed' || pd.status === 'ready') && pd.url) {
              clearInterval(pollRef.current);
              setResult({ ...res.data, url: pd.url, status: 'completed' });
              setCreating(false);
              toast.success('Podcast ready!');
              onCreated();
            } else if (pd.status === 'failed' || pd.status === 'error') {
              clearInterval(pollRef.current);
              setResult(null);
              setCreating(false);
              toast.error('Podcast generation failed. Credits will be refunded.');
            }
          } catch { /* keep polling through transient errors */ }
        }, 5000);
      } else {
        toast.success(res.data.url ? 'Podcast created!' : 'Processing started');
        setCreating(false);
        onCreated();
      }
    } catch (err) {
      setCreating(false);
      const detail = err?.response?.data?.detail;
      const isProvidersDown = err?.response?.status === 503 && typeof detail?.code === 'string' && detail.code.endsWith('_PROVIDERS_UNAVAILABLE');
      if (isProvidersDown) {
        onProvidersUnavailable(detail, { armRetry: true });
        toast.error(`${detail.message || 'Temporarily unavailable'} · Auto-retrying in 30s…`, { duration: 5000, id: 'podcast-providers-down' });
      } else {
        handleStudioError(err, { category: 'TALKING_VIDEO', idempotencyKey, fallbackMessage: 'Podcast creation failed' });
      }
    }
  };

  // Cleanup polling on unmount.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const QUALITIES = [
    { value: '480p', label: '480p — Standard' },
    { value: '720p', label: '720p — HD' },
    { value: '1080p', label: '1080p — Full HD' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="podcast-tab">
      <SubmissionUncertainDialog />
      <div className="lg:col-span-2">
        <StudioHealthBanner category="TALKING_VIDEO" health={health} cooldown={cooldown} pendingRetry={pendingRetryRef.current} />
      </div>
      <Card className="bg-white border-gray-border">
        <CardContent className="pt-4 space-y-4">
          <div><h3 className="text-sm font-bold text-text-primary">Podcast & Talking Video Generator</h3><p className="text-xs text-gray-medium">Create unlimited-length podcasts with AI-powered lip-sync</p></div>
          <div><Label className="text-xs">Video Title *</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="My Podcast Episode 1" className="mt-1 text-sm" data-testid="podcast-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Select Avatar *</Label>
              <Select value={avatarId} onValueChange={setAvatarId}><SelectTrigger className="mt-1" data-testid="podcast-avatar"><SelectValue placeholder="Choose an avatar" /></SelectTrigger>
                <SelectContent>{avatars.map(a => <SelectItem key={a.avatar_id} value={a.avatar_id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Voice Profile (Optional)</Label>
              <Select value={voiceId} onValueChange={setVoiceId}><SelectTrigger className="mt-1" data-testid="podcast-voice"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="default">Use default voice</SelectItem>{voices.map(v => <SelectItem key={v.voice_id} value={v.voice_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Quality</Label>
              <Select value={quality} onValueChange={setQuality}><SelectTrigger className="mt-1 text-xs" data-testid="podcast-quality"><SelectValue /></SelectTrigger>
                <SelectContent>{QUALITIES.map(q => <SelectItem key={q.value} value={q.value} className="text-xs">{q.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Estimated Duration (seconds)</Label>
              <Input type="number" value={duration} onChange={e => setDuration(e.target.value)} min="5" max="600" className="mt-1 text-sm" data-testid="podcast-duration" /></div>
          </div>
          <div><Label className="text-xs">Script (Text-to-Speech)</Label>
            <PromptPowerUp contextType="general">
            <Textarea value={script} onChange={e => setScript(e.target.value)} placeholder="Enter your podcast script here... (or upload audio below)"
              className="mt-1 min-h-[140px] text-sm" data-testid="podcast-script" />
            </PromptPowerUp></div>
          <Button className="w-full h-11" onClick={create} disabled={creating || !avatarId || !script.trim() || pricingLoading || !talkingRow || !health.available} data-testid="create-podcast-btn">
            {creating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Radio className="h-4 w-4 mr-2" />}
            {!health.available ? `Unavailable${cooldown > 0 ? ` · ${cooldown}s` : ''}` : (!pricingLoading && !talkingRow ? 'Unavailable' : `Create Podcast — ${pricingLoading ? '…' : formatCreditRange(talkingRow)}`)}
          </Button>
        </CardContent>
      </Card>
      <Card className="bg-white border-gray-border">
        <CardContent className="p-6">
          {result?.url ? (
            <div><video src={result.url} controls className="w-full rounded-lg mb-3" /><p className="text-sm font-medium text-text-primary mb-2 truncate">{result.title}</p>
              <div className="flex gap-2"><Button size="sm" variant="outline" className="flex-1" onClick={() => downloadFile(result.url, `konnect_podcast_${Date.now()}.mp4`)}><Download className="h-3.5 w-3.5 mr-1" />Download</Button></div></div>
          ) : result?.task_id ? (
            <ProcessingAnimation
              title="Creating Podcast"
              messages={['Generating podcast script...', 'Synthesizing voices...', 'Building conversation flow...', 'Mixing audio tracks...', 'This typically takes 2-4 minutes...']}
            />
          ) : (
            <div className="text-center py-12"><Radio className="h-16 w-16 mx-auto mb-4 text-gray-200" /><p className="text-sm text-gray-medium">Podcast video preview</p><p className="text-xs text-gray-400 mt-1">Select an avatar and enter your script to create a podcast</p></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AvatarLibraryTab({ videos, onRefresh }) {
  const deleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try { await axios.delete(`${API}/api/creative-studio/avatar-video/${id}`); toast.success('Deleted'); onRefresh(); } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="avatar-library-tab">
      {videos.length === 0 ? (
        <Card className="bg-white border-gray-border"><CardContent className="p-12 text-center">
          <FolderOpen className="h-12 w-12 mx-auto mb-3 text-gray-200" /><p className="text-sm text-gray-medium">No avatar videos yet</p><p className="text-xs text-gray-400 mt-1">Create videos from the Create or Podcast tabs</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {videos.map(v => (
            <Card key={v.video_id} className="bg-white border-gray-border hover:shadow-md transition-all" data-testid={`av-video-${v.video_id}`}>
              <div className="aspect-video bg-gray-50 relative flex items-center justify-center">
                {v.url ? <video src={v.url} className="w-full h-full object-cover" /> : (
                  <div className="flex flex-col items-center"><Clock className="h-8 w-8 text-blue-400 mb-1" /><p className="text-[10px] text-gray-medium">Processing</p></div>
                )}
                <Badge className={cn("absolute top-2 left-2 text-[9px]", v.video_type === 'podcast' ? "bg-indigo-100 text-indigo-700" : "bg-blue-100 text-blue-700")}>{v.video_type === 'podcast' ? 'Podcast' : 'Avatar'}</Badge>
              </div>
              <CardContent className="p-2.5">
                <p className="text-xs font-medium text-text-primary truncate mb-1">{v.title}</p>
                <p className="text-[10px] text-gray-medium truncate mb-2">{v.script?.substring(0, 60)}</p>
                <div className="flex items-center justify-between">
                  <Badge className="bg-gray-100 text-gray-500 text-[9px]">{v.quality || '480p'}</Badge>
                  <div className="flex gap-0.5">
                    {v.url && <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => downloadFile(v.url, `konnect_${v.video_type}_${v.video_id}.mp4`)}><Download className="h-3 w-3" /></Button>}
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => deleteVideo(v.video_id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
